import React, { ElementType, FC, forwardRef, ReactNode } from 'react';
import { Box, BoxProps } from '@mui/material';

export interface FooterSectionContactProps extends BoxProps<ElementType> {
  /**
   * Items that should be rendered in left side
   */
  contactSlot?: ReactNode;
  /**
   * Properties applied to the contact wrapper
   */
  contactSlotProps?: BoxProps<ElementType>;
  /**
   * Items that should be rendered in right side
   */
  socialSlot?: ReactNode;
  /**
   * Properties applied to the social wrapper
   */
  socialSlotProps?: BoxProps<ElementType>;
}

/**
 * Section for the main contact links and social media
 */
export const FooterSectionContact: FC<FooterSectionContactProps> = forwardRef(
  ({ sx, children, className, contactSlot, contactSlotProps, socialSlot, socialSlotProps, ...rest }, ref) => {
    const { sx: contactSlotSx, ...restContactSlot } = contactSlotProps ?? {};
    const { sx: socialSlotSx, ...restSocialSlot } = socialSlotProps ?? {};

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', lg: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: { xs: 4, lg: 2 },
          flexDirection: { xs: 'column', lg: 'row' },
          ...sx,
        }}
        ref={ref}
        data-testid="FooterSectionContact"
        className={['FlowSectionContact-root', className].filter(Boolean).join(' ')}
        {...rest}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', lg: 'center' },
            justifyContent: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            flexDirection: {
              xs: 'column',
              lg: 'row',
            },
            ...contactSlotSx,
          }}
          data-testid="FooterSectionContact.contact"
          {...restContactSlot}
        >
          {children}
          {contactSlot}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
            flexWrap: 'wrap',
            gap: 0.5,
            ...socialSlotSx,
          }}
          data-testid="FooterSectionContact.social"
          {...restSocialSlot}
        >
          {socialSlot}
        </Box>
      </Box>
    );
  },
);
